import type { AbstractPartDefinition } from "@project/emerge-types";

/**
 * AbstractPartInteraction provides a framework for defining interactions between parts.
 * These interactions run after all buildsteps have completed.
 *
 * Each part interaction processes the complete list of parts and can modify them
 * based on their relationships.
 */
export abstract class AbstractPartInteraction<
  BlueprintType = unknown,
  SettingsType = unknown,
  PartType extends
    AbstractPartDefinition<string> = AbstractPartDefinition<string>,
> {
  public interactionName: string;

  /**
   * Part types this interaction may mutate in place, or `null` when it may mutate
   * any part.
   *
   * `AbstractProduct` keeps a cached part object across builds when no interaction
   * can touch it — that preserves the object identity React memoizes on, and skips a
   * `cloneDeep` per preserved part per build. Parts whose type is listed here are
   * cloned before interactions run, so the previous build's object is never mutated.
   *
   * Declare the narrowest set that is true. Leaving it `null` stays correct but
   * makes every preserved part pay a deep clone on every build. Getting it wrong
   * in the other direction is silent corruption, so the set is typed to this
   * interaction's own part types: a misspelled one does not compile, and a
   * subclass that widens it back to `Set<string>` gives that up.
   */
  public readonly mutatedPartTypes: ReadonlySet<PartType["type"]> | null = null;

  constructor(interactionName: string) {
    this.interactionName = interactionName;
  }

  /**
   * Execute the interaction.
   * This method should identify relationships between parts and modify them accordingly.
   *
   * @param blueprint The blueprint used as reference
   * @param settings The configuration or settings
   * @param parts The complete array of parts that have been built
   * @returns The number of parts that were modified by this interaction
   */
  public abstract execute(
    blueprint: BlueprintType,
    settings: SettingsType,
    parts: PartType[],
  ): Promise<number>;

  /**
   * Helper method to find parts of a specific type
   *
   * @param parts Array of all parts
   * @param type The part type to filter for
   * @returns Array of parts matching the specified type
   */
  protected findPartsByType<T extends PartType>(
    parts: PartType[],
    type: string,
  ): T[] {
    return parts.filter((part) => part.type === type) as T[];
  }

  /**
   * Helper method to find parts that match a predicate
   *
   * @param parts Array of all parts
   * @param predicate Function that returns true for parts to include
   * @returns Array of parts matching the predicate
   */
  protected findParts<T extends PartType>(
    parts: PartType[],
    predicate: (part: PartType) => boolean,
  ): T[] {
    return parts.filter(predicate) as T[];
  }
}
